import { Link } from "react-router-dom";
import {
  ChevronRight,
  UserPlus,
  AlertTriangle,
  Clock,
  Trophy,
  CheckCircle,
} from "lucide-react";

function StudentRow({ student, rightSlot }) {
  return (
    <Link
      to={`/admin/alumnos/${student.id}`}
      className="flex items-center gap-3 p-2.5 hover:bg-gray-50 rounded-lg transition-colors"
    >
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
        {student.name
          ?.split(" ")
          .map((w) => w[0])
          .join("")
          .slice(0, 2)
          .toUpperCase() || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {student.name}
        </p>
        <p className="text-xs text-gray-500 truncate">{student.email}</p>
      </div>
      {rightSlot && (
        <div className="text-right flex-shrink-0">{rightSlot}</div>
      )}
      <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
    </Link>
  );
}

function ListCard({ icon: Icon, title, badge, color, students, empty, render }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className={color} />
          <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
        </div>
        {badge && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div className="p-2">
        {students.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle size={28} className="mx-auto text-emerald-400 mb-2" />
            <p className="text-sm text-gray-400">{empty}</p>
          </div>
        ) : (
          students.map((s) => (
            <StudentRow key={s.id} student={s} rightSlot={render?.(s)} />
          ))
        )}
      </div>
    </div>
  );
}

export default function StudentLists({
  recent,
  lowProgress,
  inactive,
  almostDone,
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ListCard
        icon={UserPlus}
        title="Recién invitados"
        color="text-indigo-600"
        students={recent}
        empty="Aún no hay alumnos"
        render={(s) => (
          <span className="text-xs text-gray-500">
            {new Date(s.created_at).toLocaleDateString("es-ES", {
              day: "numeric",
              month: "short",
            })}
          </span>
        )}
      />

      <ListCard
        icon={AlertTriangle}
        title="Bajo progreso"
        badge="< 30%"
        color="text-amber-500"
        students={lowProgress}
        empty="Todos avanzan bien"
        render={(s) => (
          <span className="text-sm font-semibold text-gray-900">
            {s.progress || 0}%
          </span>
        )}
      />

      <ListCard
        icon={Clock}
        title="Sin acceso reciente"
        badge="> 14 días"
        color="text-red-500"
        students={inactive}
        empty="Todos han entrado recientemente"
        render={(s) => (
          <span className="text-xs text-red-600 font-medium">
            {s.daysSinceLogin === null ? "Nunca" : `${s.daysSinceLogin}d`}
          </span>
        )}
      />

      <ListCard
        icon={Trophy}
        title="Casi terminan"
        badge="≥ 80%"
        color="text-emerald-600"
        students={almostDone}
        empty="Aún no hay alumnos cerca de finalizar"
        render={(s) => (
          <span className="text-sm font-semibold text-emerald-600">
            {s.progress || 0}%
          </span>
        )}
      />
    </div>
  );
}
